import { executeApiRequest } from "api";
import keys from "config/keys.jsx";
import {
  buildAiLoginPayload,
  buildAiRegisterPayload,
  buildAiSubscriptionPayload,
  buildApiKeyPayload,
  buildChatCompletionPayload
} from "domains/ai/services/aiRequestBuilders";

const getUserToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

const getAiHeaders = (includeJson = true) => {
  const userToken = getUserToken();
  return {
    ...(includeJson ? { "Content-Type": "application/json" } : {}),
    ...(keys.AI_API_KEY ? { Authorization: `Bearer ${keys.AI_API_KEY}` } : {}),
    ...(userToken ? { "X-User-Token": userToken } : {})
  };
};

class APIClient {
  request(endpoint, options = {}) {
    return executeApiRequest(keys.AI_URL, endpoint, {
      credentials: "same-origin",
      skipAuth: true,
      ...options,
      headers: {
        ...getAiHeaders(options.rawBody !== true),
        ...(options.headers || {})
      }
    });
  }

  post(endpoint, requestBody, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: "POST",
      payload: requestBody
    });
  }

  login(email, password) {
    return this.post("/api/auth/login", buildAiLoginPayload(email, password));
  }

  register(email, password, fullName) {
    return this.post("/api/auth/register", buildAiRegisterPayload(email, password, fullName));
  }

  getCurrentUser() {
    return this.request("/api/auth/me");
  }

  getAPIKeys() {
    return this.request("/api/keys");
  }

  createAPIKey(name) {
    return this.post("/api/keys", buildApiKeyPayload(name));
  }

  deleteAPIKey(keyId) {
    return this.request(`/api/keys/${encodeURIComponent(keyId)}`, {
      method: "DELETE"
    });
  }

  getUsage(days = 30) {
    return this.request("/api/analytics/usage", {
      query: { days }
    });
  }

  getDashboardMetrics() {
    return this.request("/api/analytics/dashboard");
  }

  getUsageChart(days = 7) {
    return this.request("/api/analytics/usage/chart", {
      query: { days }
    });
  }

  createSubscription(tier, paymentMethodId) {
    return this.post("/api/billing/subscribe", buildAiSubscriptionPayload(tier, paymentMethodId));
  }

  getSubscription() {
    return this.request("/api/billing/subscription");
  }

  async streamChatCompletion(
    messages,
    model = "llama-3.3-70b-versatile",
    temperature = 0.7,
    max_tokens = 20,
    onChunk
  ) {
    const data = await this.post("/v1/chat/completions", buildChatCompletionPayload({
      messages,
      model,
      temperature,
      maxTokens: max_tokens
    }));

    const content = data?.choices?.[0]?.message?.content;
    if (content && onChunk) {
      content.split(" ").forEach((word, index, words) => {
        setTimeout(() => {
          onChunk({
            choices: [{
              delta: {
                content: word + (index < words.length - 1 ? " " : "")
              }
            }]
          });
        }, index * 50);
      });
    }

    return data;
  }

  connectWebSocket() {
    return new WebSocket(`${keys.AI_URL.replace(/^http/i, "ws")}/ws/metrics`);
  }
}

export const api = new APIClient();
